import { REMIT_UNREAD } from "../../../server/rules/billing-rules";
import type { ScreenContext } from "../../shell/types";
import { foldRecord } from "../../ui/fold";
import { firstPageParams, list } from "../../ui/list";
import type { PageInfo } from "../../../server/rules/list-page";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { card, chip, download, ib, table } from "../../ui/record";
import { uploadFile } from "../../ui/upload";
import { openViewer } from "../../ui/viewer";
import { area, refuse } from "../clients/forms";
import { pageHead } from "../empty";

// Home Care › Payments (brief 1E-3, mockup v55 homecare/payments and the remittance record):
// - Upload a remittance: an 835 is read and matched line by line; an EOB or check stub is read by
//   AI while AI reading is on, else typed in. The counts (To post, Posted this month, Short or
//   denied open, Lines to rebill), Remittances and checks, and Unpaid lines with Rebill selected
//   and Write off (the void_invoice switch and a reason).
// - The posting at /homecare/payments/<id>: who paid, when, how and how much; each line's Paid,
//   Fee and Reason (short or denied need one); the lines it couldn't match, to pick or leave out;
//   the total against the deposit; Post payment. A posted payment is voided with a reason.

interface Overview extends PageInfo {
  counts: { toPost: string; postedMonth: string; shortOpen: string; toRebill: number };
  payments: {
    id: string;
    received: string;
    from: string;
    reference: string;
    method: string;
    amount: string;
    covers: string;
    status: { text: string; level: string };
  }[];
  unpaid: {
    lineId: string;
    invoiceId: string;
    invoice: string;
    who: string;
    date: string;
    description: string;
    billed: string;
    paid: string;
    open: string;
    reason: string;
    /** Brief 1E-35: the payer's code beside the reason, and whether the line was denied. */
    code: string;
    denied: boolean;
  }[];
  can: { post: boolean; writeOff: boolean };
}

interface Line {
  lineId: string;
  invoiceId: string;
  invoice: string;
  who: string;
  date: string;
  inOut: string;
  description: string;
  billed: string;
  open: string;
  paid: string;
  fee: string;
  reasonCode: string | null;
  reason: string;
  group: string | null;
  adj: string | null;
  status: { text: string; level: string };
}
interface Posting {
  id: string;
  status: "to_post" | "posted" | "void";
  /** Brief 1E-13: the payer's own number for each CMS-1500 claim the payment covers. */
  claimNumbers: {
    invoiceId: string;
    invoice: string;
    patientId: string;
    patient: string | null;
    number: string;
  }[];
  source: string;
  title: string;
  file: { id: string; name: string } | null;
  note: string | null;
  clientId: string | null;
  payerName: string | null;
  receivedOn: string | null;
  received: string | null;
  method: string | null;
  methodLabel: string | null;
  reference: string | null;
  amount: string;
  amountWords: string;
  covers: string;
  lines: Line[];
  unmatched: {
    key: string;
    account: string;
    patient: string | null;
    date: string;
    code: string;
    billed: string;
    paid: string;
    reason: string;
  }[];
  candidates: {
    invoiceId: string;
    number: string;
    client: string;
    lines: {
      lineId: string;
      date: string;
      who: string;
      description: string;
      code: string | null;
      open: string;
    }[];
  }[];
  totals: {
    billed: string;
    paid: string;
    fees: string;
    /** Brief 1E-4: an online payment's fee, Stripe's and FlowCare's apart. */
    feeParts: { stripe: string; flowcare: string } | null;
    net: string;
    matches: boolean;
  };
  clients: { id: string; name: string }[];
  methods: { id: string; label: string }[];
  reasonCodes: { code: string; words: string }[];
  void: { reason: string; at: string } | null;
  can: { edit: boolean; post: boolean; void: boolean; claimNumbers: boolean };
  today: string;
}

const tile = (k: string, v: string) =>
  el(
    "section",
    { class: "card" },
    el("div", { class: "muted small" }, k),
    el("div", { class: "tilev big" }, v),
  );
const toCents = (v: string) => Math.round(Number(v.replace(/[$,\s]/g, "") || "0") * 100);
const usd = (c: number) =>
  `${c < 0 ? "-" : ""}$${(Math.abs(c) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export async function paymentsScreen(host: HTMLElement, ctx: ScreenContext) {
  if (ctx.record) return postingRecord(host, ctx, ctx.record);
  const reading = ctx.me.ai.reading === "on";
  const picker = el("input", {
    type: "file",
    id: "rem-up",
    accept: ".835,.edi,.txt,.pdf,.jpg,.jpeg,.png,.heic,.webp",
    hidden: true,
  });
  const upLabel = el(
    "label",
    { class: "btn pri", for: "rem-up" },
    icon("upload"),
    " Upload remittance",
  );
  // Brief 1E-4: Record a payment opens the posting empty (a check with no stub): payer, method,
  // reference, amount, then the lines.
  const record = el("button", { type: "button", class: "btn" }, icon("plus"), " Record a payment");
  record.addEventListener("click", async () => {
    record.disabled = true;
    const made = await api<{ id: string }>("POST", "/api/payments", {});
    record.disabled = false;
    if (!made.ok) return toast("That didn't go through", made.body.error);
    ctx.go(`/homecare/payments/${made.body.id}`);
  });
  host.append(
    pageHead(
      ctx,
      "Post each remittance or check line by line. Anything short or denied stays open until it's rebilled or written off.",
      [record, upLabel, picker],
    ),
  );
  const body = el("div", { class: "ws" });
  host.append(body);

  picker.addEventListener("change", async () => {
    const file = picker.files?.[0];
    picker.value = "";
    if (!file) return;
    upLabel.replaceChildren(`Uploading ${file.name}…`);
    const made = await api<{ id: string }>("POST", "/api/payments", {});
    if (!made.ok) {
      upLabel.replaceChildren(icon("upload"), " Upload remittance");
      return toast("That didn't go through", made.body.error);
    }
    const up = await uploadFile(file, { kind: "payment", id: made.body.id });
    if (!up.ok) {
      upLabel.replaceChildren(icon("upload"), " Upload remittance");
      return toast("The remittance wasn't uploaded", up.error);
    }
    const read = await api<Posting>("POST", `/api/payments/${made.body.id}/read`, {
      fileId: up.file.id,
    });
    upLabel.replaceChildren(icon("upload"), " Upload remittance");
    if (!read.ok) toast("The remittance couldn't be read", read.body.error);
    else
      toast(
        `Remittance uploaded: ${file.name}`,
        read.body.lines.length
          ? `${read.body.lines.length} line${read.body.lines.length === 1 ? "" : "s"} matched · check them, then post`
          : (read.body.note ?? ""),
      );
    ctx.go(`/homecare/payments/${made.body.id}`);
  });

  async function load() {
    // Brief 1E-32: remittances and checks come one page at a time.
    const ask = (params: URLSearchParams) =>
      api<Overview>("GET", `/api/payments?${params.toString()}`);
    const res = await ask(firstPageParams("homecare/payments", ctx.me.user.id));
    if (!res.ok) {
      body.replaceChildren(
        el("p", { class: "note", role: "alert" }, "Payments couldn't be loaded."),
      );
      return;
    }
    const v = res.body;
    upLabel.hidden = !v.can.post;
    record.hidden = !v.can.post;
    const upCard = card(
      el("span", {}, icon("upload"), " Upload a remittance"),
      reading
        ? "835 file, EOB, check stub or payment report. An 835 is read as it is; AI reads an EOB or check stub. Each line is matched to the invoice line it pays."
        : "835 file, EOB, check stub or payment report. An 835 is matched line by line; type in the lines of an EOB or check stub (AI reading is off).",
      v.can.post
        ? [el("label", { class: "btn pri sm", for: "rem-up" }, icon("upload"), " Upload")]
        : [],
    );
    const ticked = new Set<string>();
    const rebill = el("button", { type: "button", class: "btn pri" }, "Rebill selected");
    const writeOff = el("button", { type: "button", class: "btn" }, "Write off");
    rebill.addEventListener("click", async () => {
      if (!ticked.size) return toast("Tick the lines to rebill");
      rebill.disabled = true;
      const r = await api<{ made: { id: string; number: string }[] }>(
        "POST",
        "/api/payments/rebill",
        { lineIds: [...ticked] },
      );
      rebill.disabled = false;
      if (!r.ok) return toast("Nothing was rebilled", r.body.error);
      const first = r.body.made[0];
      toast(
        `Rebill ${r.body.made.map((m) => m.number).join(", ")} created`,
        "Only the unpaid lines are on it",
      );
      if (first) ctx.go(`/homecare/billing/${first.id}`);
    });
    writeOff.addEventListener("click", () => {
      if (!ticked.size) return toast("Tick the lines to write off");
      const why = area("Why are they written off?", "");
      const go = el("button", { type: "button", class: "btn pri" }, "Write off");
      const m = openModal({
        title: `Write off ${ticked.size} line${ticked.size === 1 ? "" : "s"}`,
        sub: "What's open on them is closed with the reason given.",
        body: [el("div", { class: "form", style: "max-width:none" }, why.el)],
        actions: [go],
      });
      why.input.focus();
      go.addEventListener("click", async () => {
        go.disabled = true;
        const r = await api("POST", "/api/payments/write-off", {
          lineIds: [...ticked],
          reason: why.input.value,
        });
        go.disabled = false;
        if (!r.ok) return refuse(m.note, (r.body as { error?: string }).error);
        m.close();
        toast("Written off", "Logged with the reason");
        void load();
      });
    });
    body.replaceChildren(
      upCard,
      el(
        "div",
        { class: "grid4" },
        tile("To post", v.counts.toPost),
        tile("Posted this month", v.counts.postedMonth),
        tile("Short or denied, open", v.counts.shortOpen),
        tile("Lines to rebill", String(v.counts.toRebill)),
      ),
      card(
        "Remittances and checks",
        null,
        [],
        list<Overview["payments"][number]>({
          id: "homecare/payments",
          userId: ctx.me.user.id,
          noun: "payments",
          caption: "Remittances and checks",
          searchLabel: "Search payers, references or invoices",
          first: { rows: v.payments, page: v },
          source: async (params) => {
            const r = await ask(params);
            return r.ok ? { rows: r.body.payments, page: r.body } : null;
          },
          rowId: (r) => r.id,
          columns: [
            { id: "received", label: "Received", text: (r) => r.received },
            {
              id: "from",
              label: "From",
              text: (r) => r.from,
              cell: (r) => el("b", {}, r.from),
              search: true,
            },
            { id: "reference", label: "Reference", text: (r) => r.reference, search: true },
            { id: "method", label: "Method", text: (r) => r.method },
            { id: "amount", label: "Amount", num: true, text: (r) => r.amount },
            { id: "covers", label: "Covers", text: (r) => r.covers, search: true },
            {
              id: "status",
              label: "Status",
              text: (r) => r.status.text,
              cell: (r) => chip(r.status.text, r.status.level),
            },
          ],
          onOpen: (r) => ctx.go(`/homecare/payments/${r.id}`),
          empty: "Nothing yet. Upload a remittance when a payment comes in.",
        }),
      ),
      card(
        "Unpaid lines",
        "Tick lines to put them on a new invoice",
        [],
        v.unpaid.length
          ? el(
              "div",
              {},
              table(
                [
                  {
                    h: "",
                    f: (r) => {
                      const box = el("input", {
                        type: "checkbox",
                        "aria-label": `Rebill ${r.invoice} ${r.date}`,
                      });
                      box.addEventListener("change", () =>
                        box.checked ? ticked.add(r.lineId) : ticked.delete(r.lineId),
                      );
                      return box;
                    },
                  },
                  { h: "Invoice", f: (r) => el("b", {}, r.invoice) },
                  { h: "Who", f: (r) => r.who },
                  { h: "Date", f: (r) => r.date },
                  { h: "Description", f: (r) => r.description },
                  { h: "Billed", num: true, f: (r) => r.billed },
                  { h: "Paid", num: true, f: (r) => r.paid },
                  { h: "Open", num: true, f: (r) => el("b", {}, r.open) },
                  { h: "Code", f: (r) => r.code },
                  { h: "Reason", f: (r) => r.reason },
                  {
                    h: "",
                    f: (r) => {
                      if (!v.can.post || !r.denied) return "";
                      const b = el(
                        "button",
                        { type: "button", class: "btn sm pri" },
                        icon("package"),
                        " Resubmit with records",
                      );
                      b.addEventListener("click", async (e) => {
                        e.stopPropagation();
                        b.disabled = true;
                        const res = await api<{ made: { id: string; number: string }[] }>(
                          "POST",
                          "/api/payments/resubmit",
                          { lineId: r.lineId },
                        );
                        b.disabled = false;
                        if (!res.ok) return toast("That didn't go through", res.body.error);
                        const made = res.body.made[0];
                        toast(
                          "Resubmitted with records",
                          `${made?.number ?? ""}: a corrected claim with the visit notes; box 22 has the payer's claim number`,
                        );
                        if (made) ctx.go(`/homecare/billing/${made.id}`);
                      });
                      return b;
                    },
                  },
                ],
                v.unpaid,
                (r) => ctx.go(`/homecare/billing/${r.invoiceId}`),
                "Unpaid lines",
              ),
              v.can.post
                ? el(
                    "div",
                    { class: "actions", style: "margin-top:10px" },
                    rebill,
                    v.can.writeOff ? writeOff : null,
                  )
                : null,
            )
          : el("p", { class: "muted" }, "Nothing open. Every posted line is paid in full."),
      ),
    );
  }
  await load();
}

// ---- The posting ------------------------------------------------------------------------------

async function postingRecord(host: HTMLElement, ctx: ScreenContext, id: string) {
  const page = el("div", { class: "ws" });
  // Brief 1E-32: every card on the payment folds.
  foldRecord(page, "payment");
  host.append(page);

  async function load() {
    const res = await api<Posting>("GET", `/api/payments/${encodeURIComponent(id)}`);
    if (!res.ok) {
      page.replaceChildren(
        el(
          "p",
          { class: "note", role: "alert" },
          "That payment isn't there, or this role can't open it.",
        ),
      );
      return;
    }
    draw(res.body);
  }

  function draw(v: Posting) {
    ctx.crumb(v.reference ?? "To post");
    const edit = v.can.edit;
    // The draft as the office has it now: the payment's fields and each line's inputs.
    const lines = v.lines.map((l) => ({ ...l }));
    let leaveOut = new Set<string>();

    const field = (label: string, control: HTMLElement, wide = false) => {
      const fid = `pm-${label.replace(/\W/g, "").toLowerCase()}`;
      control.id = fid;
      return el(
        "div",
        { class: wide ? "field wide" : "field" },
        el("label", { for: fid }, label),
        control,
      );
    };
    const who = el(
      "select",
      {},
      el(
        "option",
        { value: "" },
        v.payerName ? `Pick the client (${v.payerName})` : "Pick the client",
      ),
      ...v.clients.map((c) => el("option", { value: c.id, selected: c.id === v.clientId }, c.name)),
    );
    const when = el("input", { type: "date", value: v.receivedOn ?? "", max: v.today });
    const how = el(
      "select",
      {},
      el("option", { value: "" }, "How it was paid"),
      ...v.methods.map((m) => el("option", { value: m.id, selected: m.id === v.method }, m.label)),
    );
    const ref = el("input", {
      type: "text",
      value: v.reference ?? "",
      placeholder: "Check #, EFT trace",
    });
    const amt = el("input", {
      type: "text",
      inputmode: "decimal",
      value: v.amount,
      placeholder: "0.00",
    });

    const totalsLine = el("span", {});
    const statusCells = new Map<string, HTMLElement>();
    function recompute() {
      let billed = 0;
      let paid = 0;
      let fees = 0;
      for (const l of lines) {
        billed += toCents(l.billed);
        paid += toCents(l.paid);
        fees += toCents(l.fee);
        const cell = statusCells.get(l.lineId);
        if (cell && edit) {
          const open = toCents(l.open);
          const p = toCents(l.paid);
          cell.replaceChildren(
            p >= open
              ? chip("Paid", "ok")
              : p === 0
                ? chip("Denied", "bad")
                : chip(`Short ${usd(open - p)}`, "warn"),
          );
        }
      }
      const net = paid - fees;
      const matches = amt.value.trim() !== "" && toCents(amt.value) === net;
      totalsLine.replaceChildren(
        v.totals.feeParts
          ? `Billed ${usd(billed)} · paid ${usd(paid)} · Stripe's fee ${v.totals.feeParts.stripe} · FlowCare's fee ${v.totals.feeParts.flowcare} · `
          : `Billed ${usd(billed)} · paid ${usd(paid)} · fees ${usd(fees)} · `,
        el("b", {}, `net ${usd(net)}`),
        " ",
        v.status === "to_post"
          ? matches
            ? chip("Matches the deposit", "ok")
            : chip("Doesn't match the deposit", "warn")
          : chip(v.status === "void" ? "Void" : "Posted", v.status === "void" ? "plain" : "ok"),
      );
    }
    amt.addEventListener("input", recompute);

    const draft = () => ({
      clientId: who.value || null,
      receivedOn: when.value || null,
      method: how.value || null,
      reference: ref.value,
      amount: amt.value,
      lines: lines.map((l) => ({
        lineId: l.lineId,
        paid: l.paid,
        fee: l.fee,
        reasonCode: l.reasonCode,
        reason: l.reason,
        group: l.group,
        adj: l.adj,
      })),
      unmatched: v.unmatched.map((u) => u.key).filter((k) => !leaveOut.has(k)),
      claimNumbers: numbers.map((n) => ({
        invoiceId: n.invoiceId,
        patientId: n.patientId,
        number: n.number,
      })),
    });
    async function save(): Promise<Posting | null> {
      const r = await api<Posting>("PUT", `/api/payments/${encodeURIComponent(id)}`, draft());
      if (!r.ok) {
        toast("That didn't save", r.body.error);
        return null;
      }
      return r.body;
    }

    // Payer claim numbers (mockup v63): the payer's own number for each CMS-1500 claim, saved when
    // the payment is posted; once posted, changed with a reason.
    const numbers = v.claimNumbers.map((n) => ({ ...n }));
    const claimCard = numbers.length
      ? card(
          el("span", {}, icon("file"), " Payer claim numbers"),
          "The payer's own number for each claim, from the remittance. A corrected claim puts it in box 22.",
          [],
          table(
            [
              {
                h: "Invoice",
                f: (n) =>
                  el(
                    "span",
                    {},
                    el("b", {}, n.invoice),
                    n.patient ? el("div", { class: "small muted" }, n.patient) : "",
                  ),
              },
              {
                h: "Payer claim number",
                f: (n) => {
                  if (!edit)
                    return el(
                      "span",
                      {},
                      n.number || "–",
                      v.can.claimNumbers
                        ? ib("pencil", "Change the payer claim number", () =>
                            claimNumberModal(n, () => void load()),
                          )
                        : "",
                    );
                  const i = el("input", {
                    type: "text",
                    maxlength: "30",
                    style: "min-width:180px",
                    value: n.number,
                    "aria-label": `Payer claim number for ${n.invoice}${n.patient ? ` ${n.patient}` : ""}`,
                  });
                  i.addEventListener("input", () => {
                    n.number = i.value.trim();
                  });
                  return i;
                },
              },
            ],
            numbers,
            undefined,
            "Payer claim numbers",
          ),
        )
      : null;
    claimCard?.setAttribute("aria-label", "Payer claim numbers");

    const lineTable = table(
      [
        { h: "Invoice", f: (l) => el("b", {}, l.invoice) },
        { h: "Who", f: (l) => l.who },
        { h: "Date", f: (l) => l.date },
        { h: "In – out", f: (l) => l.inOut },
        { h: "Description", f: (l) => l.description },
        { h: "Billed", num: true, f: (l) => l.billed },
        {
          h: "Paid",
          num: true,
          f: (l) => {
            if (!edit) return usd(toCents(l.paid));
            const i = el("input", {
              type: "text",
              inputmode: "decimal",
              value: l.paid,
              style: "width:96px",
              "aria-label": `Paid ${l.invoice} ${l.date}`,
            });
            i.addEventListener("input", () => {
              l.paid = i.value;
              recompute();
            });
            return i;
          },
        },
        {
          h: "Fee",
          num: true,
          f: (l) => {
            if (!edit) return toCents(l.fee) ? usd(toCents(l.fee)) : "–";
            const i = el("input", {
              type: "text",
              inputmode: "decimal",
              value: l.fee,
              style: "width:80px",
              "aria-label": `Fee ${l.invoice} ${l.date}`,
            });
            i.addEventListener("input", () => {
              l.fee = i.value;
              recompute();
            });
            return i;
          },
        },
        {
          h: "Code",
          f: (l) => {
            if (!edit) return l.reasonCode ?? "";
            const i = el("input", {
              type: "text",
              value: l.reasonCode ?? "",
              style: "width:72px",
              maxlength: "20",
              "aria-label": `Code ${l.invoice} ${l.date}`,
            });
            i.addEventListener("input", () => {
              l.reasonCode = i.value.trim() || null;
            });
            return i;
          },
        },
        {
          h: "Reason",
          f: (l) => {
            if (!edit) return l.reason;
            const i = el("input", {
              type: "text",
              value: l.reason,
              style: "min-width:160px",
              list: "pm-reasons",
              "aria-label": `Reason ${l.invoice} ${l.date}`,
            });
            i.addEventListener("input", () => {
              l.reason = i.value;
              const code = v.reasonCodes.find((c) => i.value.startsWith(`${c.code} · `));
              l.reasonCode = code?.code ?? l.reasonCode;
            });
            return i;
          },
        },
        {
          h: "Status",
          f: (l) => {
            const c = el("span", {}, chip(l.status.text, l.status.level));
            statusCells.set(l.lineId, c);
            return c;
          },
        },
      ],
      lines,
      undefined,
      "Payment lines",
    );
    const reasons = el(
      "datalist",
      { id: "pm-reasons" },
      ...v.reasonCodes.map((c) => el("option", { value: `${c.code} · ${c.words}` })),
    );

    // Lines the remittance had that weren't matched: pick the invoice line, or leave it out.
    const allOpen = v.candidates.flatMap((c) =>
      c.lines.map((l) => ({ ...l, invoice: c.number, invoiceId: c.invoiceId })),
    );
    const unmatched =
      edit && v.unmatched.length
        ? card(
            "Lines it couldn't match",
            "Pick the invoice line each one pays, or leave it out",
            [],
            table(
              [
                { h: "Account", f: (u) => el("b", {}, u.account) },
                { h: "Patient", f: (u) => u.patient ?? "" },
                { h: "Date", f: (u) => u.date },
                { h: "Code", f: (u) => u.code },
                { h: "Billed", num: true, f: (u) => u.billed },
                { h: "Paid", num: true, f: (u) => usd(toCents(u.paid)) },
                { h: "Reason", f: (u) => u.reason },
                {
                  h: "",
                  f: (u) => {
                    const pick = el(
                      "select",
                      { "aria-label": `Invoice line for ${u.account} ${u.date}` },
                      el("option", { value: "" }, "Pick the invoice line"),
                      ...allOpen
                        .filter((o) => !lines.some((l) => l.lineId === o.lineId))
                        .map((o) =>
                          el(
                            "option",
                            { value: o.lineId },
                            `${o.invoice} · ${o.date} · ${o.who}${o.code ? ` ${o.code}` : ""} · open $${o.open}`,
                          ),
                        ),
                    );
                    pick.addEventListener("change", async () => {
                      const o = allOpen.find((x) => x.lineId === pick.value);
                      if (!o) return;
                      lines.push({
                        lineId: o.lineId,
                        invoiceId: o.invoiceId,
                        invoice: o.invoice,
                        who: o.who,
                        date: o.date,
                        inOut: "",
                        description: o.description,
                        billed: "",
                        open: o.open,
                        paid: u.paid,
                        fee: "0.00",
                        reasonCode: null,
                        reason: u.reason,
                        group: null,
                        adj: null,
                        status: { text: "", level: "plain" },
                      });
                      leaveOut = new Set([...leaveOut, u.key]);
                      const saved = await save();
                      if (saved) draw(saved);
                    });
                    const out = el("button", { type: "button", class: "btn sm" }, "Leave out");
                    out.addEventListener("click", async () => {
                      leaveOut = new Set([...leaveOut, u.key]);
                      const saved = await save();
                      if (saved) draw(saved);
                    });
                    return el("span", { class: "rowacts" }, pick, out);
                  },
                },
              ],
              v.unmatched,
              undefined,
              "Lines it couldn't match",
            ),
          )
        : null;

    // Typing a payment in: add an invoice's open lines.
    const addInvoice =
      edit && v.candidates.length
        ? (() => {
            const s = el(
              "select",
              { "aria-label": "Add an invoice's lines" },
              el("option", { value: "" }, "Add an invoice's open lines"),
              ...v.candidates.map((c) =>
                el("option", { value: c.invoiceId }, `${c.number} · ${c.client}`),
              ),
            );
            s.addEventListener("change", async () => {
              const c = v.candidates.find((x) => x.invoiceId === s.value);
              if (!c) return;
              for (const o of c.lines)
                if (!lines.some((l) => l.lineId === o.lineId))
                  lines.push({
                    lineId: o.lineId,
                    invoiceId: c.invoiceId,
                    invoice: c.number,
                    who: o.who,
                    date: o.date,
                    inOut: "",
                    description: o.description,
                    billed: "",
                    open: o.open,
                    paid: o.open,
                    fee: "0.00",
                    reasonCode: null,
                    reason: "",
                    group: null,
                    adj: null,
                    status: { text: "Paid", level: "ok" },
                  });
              const saved = await save();
              if (saved) draw(saved);
            });
            return s;
          })()
        : null;

    const postBtn = el("button", { type: "button", class: "btn pri" }, "Post payment");
    postBtn.addEventListener("click", async () => {
      postBtn.disabled = true;
      const saved = await save();
      if (!saved) {
        postBtn.disabled = false;
        return;
      }
      const r = await api<Posting>("POST", `/api/payments/${encodeURIComponent(id)}/post`);
      postBtn.disabled = false;
      if (!r.ok) {
        draw(saved);
        return toast("Not posted yet", r.body.error);
      }
      const short = r.body.lines.filter((l) => l.status.level !== "ok").length;
      toast(
        `Posted · ${r.body.lines.length - short} lines paid, ${short} short or denied`,
        short ? "Short lines are under Unpaid lines" : "",
      );
      draw(r.body);
    });
    const unpaidBtn = el("button", { type: "button", class: "btn" }, "Open unpaid lines");
    unpaidBtn.addEventListener("click", () => ctx.go("/homecare/payments"));

    function voidModal() {
      const why = area("Why is it voided?", "");
      const go = el("button", { type: "button", class: "btn pri" }, "Void posting");
      const m = openModal({
        title: "Void this posting",
        sub: "Its lines open again on their invoices.",
        body: [el("div", { class: "form", style: "max-width:none" }, why.el)],
        actions: [go],
      });
      why.input.focus();
      go.addEventListener("click", async () => {
        go.disabled = true;
        const r = await api<Posting>("POST", `/api/payments/${encodeURIComponent(id)}/void`, {
          reason: why.input.value,
        });
        go.disabled = false;
        if (!r.ok) return refuse(m.note, r.body.error);
        m.close();
        toast("Posting voided", "Its lines are open again");
        draw(r.body);
      });
    }

    const actions = [
      ...(v.file
        ? [
            el("button", { type: "button", class: "btn" }, icon("eye"), " View remittance"),
            el("button", { type: "button", class: "btn" }, icon("download"), " Download"),
          ]
        : []),
      ...(v.can.void ? [ib("x-circle", "Void with a reason", voidModal)] : []),
    ];
    if (v.file) {
      const f = v.file;
      actions[0]?.addEventListener(
        "click",
        () => void openViewer({ file: f, document: "Remittance" }),
      );
      actions[1]?.addEventListener("click", () => void download(f.id, f.name));
    }
    const sub = [
      v.received ? `Received ${v.received}` : null,
      v.methodLabel ? `by ${v.methodLabel}` : null,
      v.amountWords,
      v.covers ? `covers ${v.covers}` : null,
    ]
      .filter(Boolean)
      .join(" · ");
    const head = el(
      "div",
      { class: "head" },
      el(
        "div",
        { class: "t" },
        el("div", { class: "eyebrow" }, `${v.file ? "Remittance" : "Payment"} · ${v.source}`),
        el("h1", {}, icon("inbox", "h1i"), v.title),
        el("div", { class: "muted" }, sub),
      ),
      el("div", { class: "actions" }, ...actions),
    );
    const note = el(
      "div",
      { class: v.status === "void" ? "note warnnote" : "note" },
      el(
        "p",
        {},
        v.status === "posted"
          ? el(
              "span",
              {},
              el("b", {}, "Posted."),
              " Short or denied lines stay open under Unpaid lines.",
            )
          : v.status === "void"
            ? el(
                "span",
                {},
                el("b", {}, "Void"),
                ` · ${v.void?.at ?? ""}: ${v.void?.reason ?? ""}. Its lines were opened again.`,
              )
            : el(
                "span",
                {},
                el("b", {}, "To post."),
                v.file
                  ? " Lines are pre-filled from the remittance. Check each one, adjust if needed, then post. Anything short stays open on the invoice."
                  : " Pick who paid, how and the amount, then add the invoice lines it pays and type what was paid on each. Anything short stays open on the invoice.",
                v.note ? noteLine(v) : null,
              ),
      ),
    );
    /**
     * The reading's note; a remittance that couldn't be read says so plainly, with Try again and
     * Type the lines in (brief 1E-35, mockup v93).
     */
    function noteLine(p: Posting) {
      if (p.note !== REMIT_UNREAD || !p.file) return el("div", { class: "small" }, p.note ?? "");
      const again = el("button", { type: "button", class: "btn sm" }, "Try again");
      again.addEventListener("click", async () => {
        again.disabled = true;
        const r = await api<Posting>("POST", `/api/payments/${encodeURIComponent(id)}/read`, {
          fileId: p.file?.id,
        });
        again.disabled = false;
        if (!r.ok) return toast("The remittance couldn't be read", r.body.error);
        draw(r.body);
      });
      const type = el("button", { type: "button", class: "btn sm ghost" }, "Type the lines in");
      type.addEventListener("click", () => {
        (page.querySelector("select, input") as HTMLElement | null)?.focus();
      });
      return el(
        "div",
        { class: "small" },
        chip("Couldn't read", "bad"),
        " ",
        el("b", {}, p.file.name),
        ` · ${REMIT_UNREAD} `,
        again,
        " ",
        type,
      );
    }
    const fields = edit
      ? card(
          "Payment",
          "Who paid, when, how and the amount deposited",
          [],
          el(
            "div",
            { class: "form grid2", style: "max-width:none;gap:10px" },
            field("Who paid", who),
            field("Received", when),
            field("How", how),
            field("Reference", ref),
            field("Amount", amt),
          ),
        )
      : null;
    page.replaceChildren(
      head,
      note,
      ...(fields ? [fields] : []),
      ...(unmatched ? [unmatched] : []),
      ...(claimCard ? [claimCard] : []),
      el(
        "section",
        { class: "card" },
        addInvoice ? el("div", { class: "cardhead" }, el("h2", {}, "Lines"), addInvoice) : null,
        lines.length
          ? lineTable
          : el(
              "p",
              { class: "muted" },
              "No lines yet. Add an invoice's open lines, then type what was paid.",
            ),
        reasons,
        el(
          "div",
          { class: "cardhead", style: "margin-top:10px" },
          totalsLine,
          v.can.post ? postBtn : v.status === "posted" ? unpaidBtn : null,
        ),
      ),
    );
    recompute();
  }

  await load();
}

/** A posted claim's payer claim number, changed with a reason (brief 1E-13). */
function claimNumberModal(
  n: { invoiceId: string; invoice: string; patientId: string; number: string },
  done: () => void,
) {
  const num = el("input", { type: "text", maxlength: "30", value: n.number, id: "pcn-n" });
  const why = area("Why", "");
  const go = el("button", { type: "button", class: "btn pri" }, "Save");
  const m = openModal({
    title: "Payer claim number",
    sub: n.invoice,
    body: [
      el(
        "div",
        { class: "form", style: "max-width:none" },
        el("div", { class: "field" }, el("label", { for: "pcn-n" }, "Payer claim number"), num),
        why.el,
      ),
    ],
    actions: [go],
  });
  go.addEventListener("click", async () => {
    go.disabled = true;
    const r = await api("PUT", `/api/invoices/${encodeURIComponent(n.invoiceId)}/claim-numbers`, {
      patientId: n.patientId,
      number: num.value,
      why: why.input.value,
    });
    go.disabled = false;
    if (!r.ok) return refuse(m.note, (r.body as { error?: string }).error);
    m.close();
    toast("Payer claim number saved", "In the history");
    done();
  });
}
